import { HttpErrorResponse, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { SentryHttpErrorHandlerService } from '@core/services';
import { InfoMessagesService } from '@features/info-messages';
import { AuthenticationService } from '@features/server-side-authentication';
import { BehaviorSubject, catchError, filter, Observable, of, switchMap, take, throwError } from 'rxjs';
import { HttpErrorStrategy } from '../models/http-error-strategy.interface';

interface ExcludedEndpoint {
  readonly pattern: string;
  readonly methods: readonly string[];
  readonly description?: string;
}
class NetworkErrorStrategy implements HttpErrorStrategy {
  constructor(
    private sentryHandler: SentryHttpErrorHandlerService,
    private infoMessages: InfoMessagesService,
  ) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === 0;
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    this.infoMessages.showError('Unexpected error. Please try again later.');
    this.sentryHandler.captureNetworkError(request);
  }
}

class TokenRefreshRetryStrategy implements HttpErrorStrategy {
  private static isRefreshing = false;
  private static refreshTokenSubject = new BehaviorSubject<any>(null);

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private sentryHandler: SentryHttpErrorHandlerService,
  ) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.Unauthorized && error.error?.code === 'token_not_valid';
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): Observable<any> {
    if (request.url.includes('/api/auth/token/refresh/')) {
      this.sentryHandler.addNavigationBreadcrumb(
        'Token refresh failed - redirecting to logout',
        request.url,
        '/auth/logout',
      );
      this.router.navigate(['/auth/logout']);
      return throwError(() => error);
    }

    if (TokenRefreshRetryStrategy.isRefreshing) {
      return TokenRefreshRetryStrategy.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap(() => of({ shouldRetry: true })),
      );
    }

    TokenRefreshRetryStrategy.isRefreshing = true;
    TokenRefreshRetryStrategy.refreshTokenSubject.next(null);
    return this.authService.restoreToken().pipe(
      switchMap((refreshSuccess) => {
        TokenRefreshRetryStrategy.isRefreshing = false;

        if (refreshSuccess) {
          TokenRefreshRetryStrategy.refreshTokenSubject.next(true);
          return of({ shouldRetry: true });
        } else {
          this.sentryHandler.addNavigationBreadcrumb(
            'Token refresh failed - redirecting to logout',
            request.url,
            '/auth/logout',
          );
          this.router.navigate(['/auth/logout']);
          return throwError(() => error);
        }
      }),
      catchError((refreshError) => {
        TokenRefreshRetryStrategy.isRefreshing = false;
        this.router.navigate(['/auth/logout']);
        return throwError(() => refreshError);
      }),
    );
  }
}

class UnauthorizedErrorStrategy implements HttpErrorStrategy {
  constructor(
    private router: Router,
    private sentryHandler: SentryHttpErrorHandlerService,
  ) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.Unauthorized;
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    if (
      (error.error.code === 'user_inactive' || error.error.code === 'user_not_found') &&
      !request.url.includes('/me')
    ) {
      this.sentryHandler.addNavigationBreadcrumb(
        'Unauthorized access - redirecting to logout',
        request.url,
        '/auth/logout',
      );

      this.router.navigate(['/auth/logout']);
    }
  }
}

class BusinessLogicErrorStrategy implements HttpErrorStrategy {
  constructor(private sentryHandler: SentryHttpErrorHandlerService) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.ExpectationFailed;
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    this.sentryHandler.captureBusinessLogicError(error, request);
  }
}

class NotFoundErrorStrategy implements HttpErrorStrategy {
  constructor(
    private router: Router,
    private sentryHandler: SentryHttpErrorHandlerService,
  ) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.NotFound;
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    this.sentryHandler.addNavigationBreadcrumb(
      '404 error - redirecting to not found page',
      request.url,
      'page-not-found',
    );

    this.router.navigate(['page-not-found'], { skipLocationChange: true });
  }
}

class ValidationErrorStrategy implements HttpErrorStrategy {
  private static readonly EXCLUDED_ENDPOINTS: readonly ExcludedEndpoint[] = [
    // VPN Configuration endpoints
    { pattern: '/api/vpn-configuration/', methods: ['POST', 'PATCH'], description: 'VPN setup validation' },

    // User management endpoints
    { pattern: '/api/users/', methods: ['POST', 'PUT'], description: 'User management validation' },

    // Pipelines endpoints
    { pattern: '/api/pipelines/', methods: ['POST', 'PATCH'], description: 'Pipeline configuration validation' },

    // Departments endpoints
    { pattern: '/api/departments/', methods: ['POST', 'PUT'], description: 'Department management validation' },

    // Data Sources endpoints
    { pattern: '/api/data-source-tables/', methods: ['PUT', 'PATCH'], description: 'Data source tables validation' },
    { pattern: '/api/data-source-columns/', methods: ['PATCH'], description: 'Data source columns validation' },
    { pattern: '/api/data-sources/', methods: ['POST', 'PUT', 'PATCH'], description: 'Data sources validation' },

    // Connectors endpoints
    { pattern: '/api/connectors/', methods: ['POST', 'PATCH'], description: 'Connectors validation' },

    // Login endpoints
    { pattern: '/api/auth/login/', methods: ['GET'], description: 'Authentication validation' },
  ] as const;

  constructor(private sentryHandler: SentryHttpErrorHandlerService) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.BadRequest;
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    if (!this.isExcludedFromSentryReporting(request.url, request.method)) {
      this.sentryHandler.captureValidationError(error, request);
    }
  }

  private isExcludedFromSentryReporting(url: string, method: string): boolean {
    const normalizedMethod = method.toUpperCase();

    return ValidationErrorStrategy.EXCLUDED_ENDPOINTS.some((endpoint) => {
      const containsPattern = url.includes(endpoint.pattern);
      const hasMatchingMethod = endpoint.methods.includes(normalizedMethod);

      return containsPattern && hasMatchingMethod;
    });
  }
}

class ForbiddenErrorStrategy implements HttpErrorStrategy {
  constructor(private sentryHandler: SentryHttpErrorHandlerService) {}

  canHandle(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.Forbidden;
  }

  handle(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    this.sentryHandler.capturePermissionError(request);
  }
}

export class HttpErrorStrategyFactory {
  private readonly sentryHandler = inject(SentryHttpErrorHandlerService);
  private readonly router = inject(Router);
  private readonly infoMessages = inject(InfoMessagesService);
  private readonly authService = inject(AuthenticationService);

  createStrategies(): HttpErrorStrategy[] {
    return [
      new NetworkErrorStrategy(this.sentryHandler, this.infoMessages),
      new TokenRefreshRetryStrategy(this.authService, this.router, this.sentryHandler), // Add this before general unauthorized
      new UnauthorizedErrorStrategy(this.router, this.sentryHandler),
      new BusinessLogicErrorStrategy(this.sentryHandler),
      new NotFoundErrorStrategy(this.router, this.sentryHandler),
      new ValidationErrorStrategy(this.sentryHandler),
      new ForbiddenErrorStrategy(this.sentryHandler),
    ];
  }
}
